import React from "react";
import PoemCard from "@/components/PoemCard";
import { cn } from "@/lib/utils";

interface PoemPairingProps {
  poet1: string;
  poet2: string;
  poemTitle1: string;
  poemTitle2: string;
  poemContent1: string;
  poemContent2: string;
  handlePoemWin: (position: number) => void;
  position1: number;
  position2: number;
  winner: number | null;
  isRoundLocked?: boolean;
  isPoemArchived1?: boolean;
  isPoemArchived2?: boolean;
}

// Ensure content is a valid string
const ensureValidContent = (content: string, title: string): string => {
  if (!content || content.trim() === '') {
    return `[No content available for "${title}"]`;
  }
  return content;
};

const PoemPairing = ({
  poet1,
  poet2,
  poemTitle1,
  poemTitle2,
  poemContent1,
  poemContent2,
  handlePoemWin,
  position1,
  position2,
  winner,
  isRoundLocked = false,
  isPoemArchived1 = false,
  isPoemArchived2 = false,
}: PoemPairingProps) => {
  // Process content to ensure it's valid
  const displayContent1 = ensureValidContent(poemContent1, poemTitle1);
  const displayContent2 = ensureValidContent(poemContent2, poemTitle2);
  
  return (
    <div className="fade-scale">
      <h2 className="text-2xl font-poetry text-center mb-6">Decide which poem moves on</h2>
      
      <div className={cn(
        "grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto",
        winner !== null && "relative"
      )}>
        {winner !== null && isRoundLocked && (
          <div className="absolute inset-0 flex justify-center items-center pointer-events-none md:hidden z-10">
            <div className="bg-amber-100 px-4 py-2 rounded-full shadow-md">
              <span className="text-amber-800 font-semibold">Selection recorded</span>
            </div>
          </div>
        )}
        
        <div className={cn(
          "transition-all duration-300",
          winner === position1 ? "md:scale-105 z-10" : "",
          winner !== null && winner !== position1 ? "md:scale-95" : ""
        )}>
          <PoemCard 
            poet={poet1} 
            poemTitle={poemTitle1}
            poemContent={displayContent1}
            onWin={() => handlePoemWin(position1)}
            isWinner={winner === position1}
            hasWinner={isRoundLocked}
            isArchived={isPoemArchived1}
          />
        </div>
        
        <div className={cn(
          "transition-all duration-300",
          winner === position2 ? "md:scale-105 z-10" : "",
          winner !== null && winner !== position2 ? "md:scale-95" : ""
        )}>
          <PoemCard 
            poet={poet2} 
            poemTitle={poemTitle2}
            poemContent={displayContent2}
            onWin={() => handlePoemWin(position2)}
            isWinner={winner === position2}
            hasWinner={isRoundLocked}
            isArchived={isPoemArchived2}
          />
        </div>
      </div>
    </div>
  );
};

export default PoemPairing;
